class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.hideTimeout = null;
    this.activeItem = null;
  }

  connectedCallback() {
    this.init();
    this.handleResize = () => {
      if (this.activeItem) {
        const submenu = this.activeItem.parentElement?.querySelector('[ref="submenu[]"]');
        if (submenu && submenu.hasAttribute('data-small-popup')) {
          this.adjustSmallPopupPosition(submenu);
        }
      }
    };
    
    this.handleGlobalClick = (e) => {
      if (this.activeItem && !this.contains(e.target)) {
        const submenu = this.activeItem.parentElement?.querySelector('[ref="submenu[]"]');
        if (submenu) {
          this.hideSubmenu(this.activeItem, submenu, true);
        }
      }
    };
    
    this.handleGlobalKeydown = (e) => {
      if (e.key === 'Escape' && this.activeItem) {
        const submenu = this.activeItem.parentElement?.querySelector('[ref="submenu[]"]');
        if (submenu) {
          this.hideSubmenu(this.activeItem, submenu, true);
        }
      }
    };
    
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleGlobalClick);
    document.addEventListener('keydown', this.handleGlobalKeydown);
  }

  disconnectedCallback() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    if (this.handleGlobalClick) {
      document.removeEventListener('click', this.handleGlobalClick);
    }
    if (this.handleGlobalKeydown) {
      document.removeEventListener('keydown', this.handleGlobalKeydown);
    }
  }

  init() {
    const popupTriggers = this.querySelectorAll('[aria-haspopup="true"]');
    
    popupTriggers.forEach(trigger => {
      const submenu = trigger.parentElement?.querySelector('[ref="submenu[]"]');
      
      if (submenu) {
        const show = () => this.showSubmenu(trigger, submenu);
        const hide = () => this.hideSubmenu(trigger, submenu);

        trigger.addEventListener('mouseenter', show);
        trigger.addEventListener('focus', show);
        submenu.addEventListener('mouseenter', show);
        
        trigger.addEventListener('mouseleave', hide);
        trigger.addEventListener('blur', hide);
        submenu.addEventListener('mouseleave', hide);
        
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleSubmenu(trigger, submenu);
          }
        });
        
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleSubmenu(trigger, submenu);
        });
      }
    });
  }

  showSubmenu(trigger, submenu) {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.activeItem && this.activeItem !== trigger) {
      const otherSubmenu = this.activeItem.parentElement?.querySelector('[ref="submenu[]"]');
      if (otherSubmenu) {
        this.activeItem.setAttribute('aria-expanded', 'false');
        otherSubmenu.classList.remove('header-popup__panel--visible');
        this.activeItem = null;
      }
    }

    this.activeItem = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    submenu.classList.add('header-popup__panel--visible');
    
    if (submenu.hasAttribute('data-small-popup')) {
      this.adjustSmallPopupPosition(submenu);
    }
  }

  /**
   * Hides a submenu.
   * @param {HTMLElement} trigger - The menu trigger element
   * @param {HTMLElement} submenu - The submenu panel
   * @param {boolean} immediate - If true, hides instantly. If false, uses a delay.
   */
  hideSubmenu(trigger, submenu, immediate = false) {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    const doHide = () => {
      trigger.setAttribute('aria-expanded', 'false');
      submenu.classList.remove('header-popup__panel--visible');
      
      if (this.activeItem === trigger) {
        this.activeItem = null;
      }
      this.hideTimeout = null;
    };
    
    if (immediate) {
      doHide();
    } else {
      this.hideTimeout = setTimeout(doHide, 100);
    }
  }

  toggleSubmenu(trigger, submenu) {
    if (this.activeItem === trigger) {
      this.hideSubmenu(trigger, submenu, true);
    } else {
      this.showSubmenu(trigger, submenu);
    }
  }

  adjustSmallPopupPosition(submenu) {
    const viewportWidth = window.innerWidth;
    const documentWidth = document.documentElement.clientWidth;
    const scrollbarWidth = viewportWidth - documentWidth;
    const availableViewportWidth = viewportWidth - scrollbarWidth;
    const viewportCenter = availableViewportWidth / 2;
    
    const popupRect = submenu.getBoundingClientRect();
    const popupLeft = popupRect.left;
    
    submenu.style.width = '';
    
    const isInFirstHalf = popupLeft < viewportCenter;
    
    if (isInFirstHalf) {
      const availableWidth = viewportCenter - popupLeft;
      const finalWidth = Math.min(availableWidth, 447);
      submenu.style.width = `${finalWidth}px`;
    } else {
      const availableWidth = availableViewportWidth - popupLeft;
      const finalWidth = Math.min(availableWidth, 447);
      submenu.style.width = `${finalWidth}px`;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
